// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/modals/channel_tabs/ChannelOverviewTab.module.css';
import type {FormInputs} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/shared';
import type {Channel} from '@app/features/channel/models/Channel';
import type {Guild} from '@app/features/guild/models/Guild';
import {MATURE_CONTENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {
	getEffectiveChannelContentWarning,
	resolveEffectiveChannelMatureContent,
} from '@app/features/messaging/utils/ContentWarningUtils';
import {Textarea} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {CONTENT_WARNING_TEXT_MAX_LENGTH, ContentWarningLevel} from '@fluxer/constants/src/GuildConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {Controller, type UseFormReturn} from 'react-hook-form';

const CATEGORY_DESCRIPTOR = msg({
	message: 'category',
	comment: 'Lowercase source label for an inherited mature-content setting.',
});
const COMMUNITY_DESCRIPTOR = msg({
	message: 'community',
	comment: 'Lowercase source label for an inherited mature-content setting.',
});
const INHERITS_FROM_ON_DESCRIPTOR = msg({
	message: 'Inherits from {inheritedSourceLabel}: on',
	comment: 'Mature-content setting summary showing that the inherited setting is enabled.',
});
const INHERITS_FROM_OFF_DESCRIPTOR = msg({
	message: 'Inherits from {inheritedSourceLabel}: off',
	comment: 'Mature-content setting summary showing that the inherited setting is disabled.',
});
const INHERIT_DESCRIPTOR = msg({
	message: 'Inherit',
	comment: 'Mature-content option that uses the parent or community setting.',
});
const ON_DESCRIPTOR = msg({
	message: 'On',
	comment: 'Mature-content option that marks this channel for mature content.',
});
const MARKS_THIS_CHANNEL_FOR_MATURE_CONTENT_DESCRIPTOR = msg({
	message: 'Marks this channel for mature content.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const OFF_2_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'Mature-content option that leaves this channel ungated.',
});
const NOT_GATED_EVEN_IF_THE_PARENT_HAS_IT_DESCRIPTOR = msg({
	message: 'No mature content gate, even if the parent has it on.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const THIS_CONTAINS_SENSITIVE_CONTENT_DESCRIPTOR = msg({
	message: 'This contains sensitive content.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const MATURE_CONTENT_OVERRIDE_DESCRIPTOR = msg({
	message: 'Mature content override',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const OVERRIDE_CATEGORY_MATURE_CONTENT_SETTING_DESCRIPTOR = msg({
	message:
		'Override the {inheritedSourceLabel}-level setting for this category. Mature content is shown behind a gate before entry.',
	comment:
		'Description for the mature content setting in category settings. Preserve {inheritedSourceLabel}; it is inserted by code.',
});
const OVERRIDE_CHANNEL_MATURE_CONTENT_SETTING_DESCRIPTOR = msg({
	message:
		'Override the {inheritedSourceLabel}-level setting for this channel. Mature content is shown behind a gate before entry.',
	comment:
		'Description for the mature content setting in channel settings. Preserve {inheritedSourceLabel}; it is inserted by code.',
});
const SHOW_A_CONTENT_WARNING_IN_THIS_CHANNEL_DESCRIPTOR = msg({
	message: 'Show a content warning in this channel',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const MEMBERS_SEE_A_CONSENT_PROMPT_BEFORE_ENTERING_DESCRIPTOR = msg({
	message: 'Members see a consent prompt before entering.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const CUSTOM_WARNING_TEXT_DESCRIPTOR = msg({
	message: 'Custom warning text',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});

interface MatureContentSectionProps {
	form: UseFormReturn<FormInputs>;
	channel: Channel;
	guild: Guild | null | undefined;
	isCategory: boolean;
}

export const MatureContentSection: React.FC<MatureContentSectionProps> = ({form, channel, guild, isCategory}) => {
	const {i18n} = useLingui();
	const stubForInherit = {
		...channel,
		nsfwOverride: null,
		contentWarningLevel: ContentWarningLevel.INHERIT,
		contentWarningText: null,
	} as typeof channel;
	const inheritedMatureContent = resolveEffectiveChannelMatureContent(stubForInherit, guild);
	const inheritedWarning = getEffectiveChannelContentWarning(stubForInherit, guild);
	const inheritedSourceLabel =
		inheritedMatureContent.source === 'parent'
			? i18n._(CATEGORY_DESCRIPTOR)
			: inheritedMatureContent.source === 'guild'
				? i18n._(COMMUNITY_DESCRIPTOR)
				: i18n._(COMMUNITY_DESCRIPTOR);
	const inheritedDescription = inheritedMatureContent.value
		? i18n._(INHERITS_FROM_ON_DESCRIPTOR, {
				inheritedSourceLabel,
			})
		: i18n._(INHERITS_FROM_OFF_DESCRIPTOR, {
				inheritedSourceLabel,
			});
	const matureContentOptions: ReadonlyArray<RadioOption<string>> = [
		{
			value: 'inherit',
			name: i18n._(INHERIT_DESCRIPTOR),
			desc: inheritedDescription,
		},
		{
			value: 'on',
			name: i18n._(ON_DESCRIPTOR),
			desc: i18n._(MARKS_THIS_CHANNEL_FOR_MATURE_CONTENT_DESCRIPTOR),
		},
		{
			value: 'off',
			name: i18n._(OFF_2_DESCRIPTOR),
			desc: i18n._(NOT_GATED_EVEN_IF_THE_PARENT_HAS_IT_DESCRIPTOR),
		},
	];
	const overrideToKey = (v: boolean | null): string => (v === null ? 'inherit' : v ? 'on' : 'off');
	const keyToOverride = (k: string): boolean | null => (k === 'inherit' ? null : k === 'on');
	const inheritedWarningText = inheritedWarning.text ?? i18n._(THIS_CONTAINS_SENSITIVE_CONTENT_DESCRIPTOR);
	return (
		<>
			<div data-flx="channel.channel-tabs.channel-overview-tab.div--5">
				<div className={styles.fieldLabel} data-flx="channel.channel-tabs.channel-overview-tab.field-label--3">
					{i18n._(MATURE_CONTENT_DESCRIPTOR)}
				</div>
				<p
					className={styles.fieldDescription}
					style={{marginBottom: '1rem'}}
					data-flx="channel.channel-tabs.channel-overview-tab.field-description--2"
				>
					{isCategory
						? i18n._(OVERRIDE_CATEGORY_MATURE_CONTENT_SETTING_DESCRIPTOR, {inheritedSourceLabel})
						: i18n._(OVERRIDE_CHANNEL_MATURE_CONTENT_SETTING_DESCRIPTOR, {inheritedSourceLabel})}
				</p>
				<Controller
					name="nsfw_override"
					control={form.control}
					render={({field}) => (
						<RadioGroup<string>
							options={matureContentOptions}
							value={overrideToKey(field.value)}
							onChange={(v) => field.onChange(keyToOverride(v))}
							aria-label={i18n._(MATURE_CONTENT_OVERRIDE_DESCRIPTOR)}
							data-flx="channel.channel-tabs.channel-overview-tab.radio-group.change"
						/>
					)}
					data-flx="channel.channel-tabs.channel-overview-tab.controller--5"
				/>
			</div>
			<Controller
				name="content_warning_level"
				control={form.control}
				render={({field}) => (
					<Switch
						label={i18n._(SHOW_A_CONTENT_WARNING_IN_THIS_CHANNEL_DESCRIPTOR)}
						description={i18n._(MEMBERS_SEE_A_CONSENT_PROMPT_BEFORE_ENTERING_DESCRIPTOR)}
						value={field.value === ContentWarningLevel.CONTENT_WARNING}
						onChange={(value) =>
							field.onChange(value ? ContentWarningLevel.CONTENT_WARNING : ContentWarningLevel.INHERIT)
						}
						data-flx="channel.channel-tabs.channel-overview-tab.switch.change"
					/>
				)}
				data-flx="channel.channel-tabs.channel-overview-tab.controller--6"
			/>
			{form.watch('content_warning_level') === ContentWarningLevel.CONTENT_WARNING && (
				<Controller
					name="content_warning_text"
					control={form.control}
					render={({field}) => (
						<Textarea
							label={i18n._(CUSTOM_WARNING_TEXT_DESCRIPTOR)}
							placeholder={inheritedWarningText}
							value={field.value ?? ''}
							onChange={(e) => field.onChange(e.target.value)}
							maxLength={CONTENT_WARNING_TEXT_MAX_LENGTH}
							minRows={2}
							maxRows={4}
							showCharacterCount={true}
							data-flx="channel.channel-tabs.channel-overview-tab.textarea.change"
						/>
					)}
					data-flx="channel.channel-tabs.channel-overview-tab.controller--7"
				/>
			)}
		</>
	);
};
